import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withStateHandlers,
} from 'recompose';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { isEmpty } from 'utils/types';
import { constants as addressesConstants } from 'utils/addresses';

import { selectors as settingsSelectors } from 'state/settings';
import {
  actions as addressActions,
  selectors as addressSelectors,
} from 'state/addresses';
import { selectors as shipmentSelectors } from 'state/shipments';
import { selectors as warningsSelectors } from 'state/warnings';
import { selectors as errorsSelectors } from 'state/errors';
import { selectors as uiSelectors } from 'state/ui';

import AddressModal from 'components/AddressModal/styled';

const computeFormData = ({
  zipcode,
  company_name,
  address_line,
  contact_name,
  contact_phone,
  city,
  country,
  latitude,
  longitude,
  gid,
}) => ({
  zipcode: zipcode && zipcode.trim().replace(/\s/g, ''),
  company_name: company_name && company_name.trim().replace(/\s+/g, ' '),
  address_line: address_line && address_line.trim().replace(/\s+/g, ' '),
  contact_name: contact_name && contact_name.trim().replace(/\s+/g, ' '),
  contact_phone: contact_phone && contact_phone.replace(/\s/g, ''),
  country: country,
  gid,
  latitude,
  longitude,
  ...(!isEmpty(city) && { city: city.trim() }),
});

const enhanceCommon = ({
  connect: { mapStateToProps, mapActionToProps },
  handleSubmit,
  handleSubmitDelete,
}) =>
  compose(
    withTranslation('AddressFormModal'),
    connect(mapStateToProps, mapActionToProps),
    withProps(
      ({
        addressSuggestionsEnabled = true,
        address: {
          zipcode,
          company_name,
          address_line,
          contact_name,
          city,
          contact_phone,
          id,
          country,
          latitude,
          longitude,
          gid,
        },
      }) => {
        return {
          addressSuggestionsEnabled,
          names: addressesConstants.FIELDS_NAMES,
          isNewAddress: !id,
          zipcode,
          city,
          company_name,
          address_line,
          contact_name,
          contact_phone,
          country,
          latitude,
          longitude,
          id,
          gid,
          // Once enabled for other countries move this options to BE returning
          // options based on shipper country
          countryOptions: [
            { value: 'BE' },
            { value: 'CZ' },
            { value: 'DE' },
            { value: 'ES' },
            { value: 'FR' },
            { value: 'NL' },
            { value: 'PL' },
            { value: 'PT' },
            { value: 'SI' },
            { value: 'UK' },
          ],
        };
      }
    ),
    withStateHandlers(
      ({
        zipcode,
        company_name,
        address_line,
        contact_name,
        contact_phone,
        city,
        country,
        shipperCountry,
        gid,
      }) => ({
        zipcode,
        company_name,
        address_line,
        contact_name,
        contact_phone,
        city,
        gid,
        country: country || shipperCountry,
      }),
      {
        handleFieldChange:
          () =>
          (_, { name, value }) => ({
            [name]: value,
          }),
        updateFormFromAddressSuggestion:
          ({ country, zipcode }) =>
          (addressSuggestion) => {
            return {
              zipcode: addressSuggestion.zipcode || zipcode,
              country,
              address_line: addressSuggestion.address_line,
              company_name: addressSuggestion.company_name,
              latitude: addressSuggestion.latitude,
              longitude: addressSuggestion.longitude,
              gid: addressSuggestion.gid,
            };
          },
        resetPOIData: () => () => ({
          latitude: null,
          longitude: null,
          gid: null,
        }),
      }
    ),
    withHandlers(({ zipcodeTyped, country }) => ({
      handleSuggestionSelected:
        ({ updateFormFromAddressSuggestion, company_name }) =>
        (addressSuggestion) => {
          const companyName = addressSuggestion.company_name || company_name;
          updateFormFromAddressSuggestion({
            ...addressSuggestion,
            address_line: addressSuggestion.fullAddress,
            company_name: companyName,
          });

          if (addressSuggestion.zipcode) {
            zipcodeTyped({
              country,
              zipcode: addressSuggestion.zipcode,
            });
          }
        },
      handleCompanyNameChange:
        ({ handleFieldChange }) =>
        (_, { name, value }) => {
          handleFieldChange(_, {
            name,
            value: value || '',
          });
        },
      handleAddressLineChange:
        ({ handleFieldChange, resetPOIData }) =>
        (_, { name, value }) => {
          handleFieldChange(_, {
            name,
            value: value || '',
          });
          resetPOIData();
        },
      handleZipcodeChange:
        ({ handleFieldChange, resetPOIData, zipcodeTyped, country }) =>
        (_, { name, value }) => {
          zipcodeTyped({
            country,
            zipcode: (value || '').trim().replace(/\s/g, '').toUpperCase(),
          });
          handleFieldChange(_, {
            name,
            value: (value || '').trim().replace(/\s/g, '').toUpperCase(),
          });
          resetPOIData();
        },
      handleCountryChange:
        ({ handleFieldChange, resetPOIData, zipcodeTyped, zipcode }) =>
        ({ value }) => {
          handleFieldChange(null, {
            name: addressesConstants.FIELDS_NAMES.country,
            value: value || '',
          });

          if (!!zipcode && zipcode.length) {
            zipcodeTyped({
              country: value,
              zipcode: zipcode || '',
            });
          }
          resetPOIData();
        },
      handleClose:
        ({ onClose }) =>
        () => {
          onClose();
        },
      handleSubmit,
      handleSubmitDelete,
    })),
    lifecycle({
      componentDidMount() {
        const { zipcode, country, zipcodeTyped } = this.props;
        if (!isEmpty(zipcode)) {
          zipcodeTyped({ zipcode, country });
        }
      },
    })
  );

const AddressGlobalModal = enhanceCommon({
  connect: {
    mapStateToProps: (state) => {
      const { country } = settingsSelectors.getCountrySettings(state);
      return {
        shipperCountry: country,
        zipcode_city: addressSelectors.getCityLabel(state),
        warnings: warningsSelectors.getWarnings(state),
        errors: errorsSelectors.getErrors(state),
        isZipCodeEnabled: addressSelectors.isZipCodeEnabled(state),
      };
    },
    mapActionToProps: {
      zipcodeTyped: addressActions.zipcodeTyped,
      onSubmit: addressActions.submitCreateAddress,
    },
  },
  handleSubmitDelete:
    ({ handleDelete }) =>
    () => {
      handleDelete();
    },
  handleSubmit:
    ({
      onSubmit,
      zipcode,
      company_name,
      address_line,
      contact_name,
      contact_phone,
      country,
      latitude,
      longitude,
      city,
      id,
      gid,
      onClose,
    }) =>
    () => {
      const formData = {
        ...computeFormData({
          country,
          zipcode,
          company_name,
          address_line,
          contact_name,
          contact_phone,
          city,
          latitude,
          longitude,
          gid,
        }),
        ...(!!id && { id }),
      };

      onSubmit({ formData, onClose });
    },
})(AddressModal);

const AddressFormModal = enhanceCommon({
  connect: {
    mapStateToProps: (state, { shipmentKey, stopType }) => {
      const { country } = settingsSelectors.getCountrySettings(state);
      const isEditMode = uiSelectors.isAddressModalEditMode(state);
      const baseAddress = {
        zipcode: '',
        company_name: '',
        address_line: '',
        contact_name: '',
        contact_phone: '',
        city: '',
        country,
      };

      return {
        shipperCountry: country,
        zipcode_city: addressSelectors.getCityLabel(state),
        warnings: warningsSelectors.getWarnings(state),
        errors: errorsSelectors.getErrors(state),
        isZipCodeEnabled: addressSelectors.isZipCodeEnabled(state),
        address: isEditMode
          ? shipmentSelectors.getShipmentAddress(
              state,
              shipmentKey,
              stopType
            ) || baseAddress
          : baseAddress,
      };
    },
    mapActionToProps: {
      zipcodeTyped: addressActions.zipcodeTyped,
      onSubmit: addressActions.submitCreateAddressForShipment,
      onClose: addressActions.closeCreateAddress,
    },
  },
  handleSubmit:
    ({
      onSubmit,
      zipcode,
      city,
      company_name,
      address_line,
      contact_name,
      contact_phone,
      country,
      shipmentKey,
      stopType,
      latitude,
      longitude,
      gid,
    }) =>
    () => {
      const formData = {
        ...computeFormData({
          country,
          zipcode,
          company_name,
          address_line,
          contact_name,
          contact_phone,
          city,
          latitude,
          longitude,
          gid,
        }),
      };
      onSubmit({ formData, shipmentKey, stopType });
    },
})(AddressModal);

export { AddressFormModal, AddressGlobalModal };
export default undefined;
